import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { BiPhone } from "react-icons/bi";
import { RiCompass3Line } from "react-icons/ri";
import { LuLightbulb } from "react-icons/lu";
import { PiTrophyBold } from "react-icons/pi";
import useKycDetails from "../../../hooks/useKycDetails";
import BuyAirtime from "./airtime/BuyAirtime";
import AirtimePreview from "./airtime/AirtimePreview";
import BuySuccess from "./airtime/BuySuccess";
import BuyData from "./data/BuyData";
import DataPreview from "./data/DataPreview";
import DataSuccess from "./data/DataSuccess";
import BuyElectricity from "./electricity/BuyElectricity";
import ElectricPreview from "./electricity/ElectricPreview";
import ElectricSuccess from "./electricity/ElectricSuccess";
import Betting from "./betting/Betting";
import BettingPreview from "./betting/BettingPreview";
import BettingSuccess from "./betting/BettingSuccess";
import { BettingPaymentSource } from "./betting/types";
import { ElectricityReceipt } from "./electricity/types";
import { DEFAULT_UTILITY_PAYMENT_SOURCE } from "../../../constants/constants";

const isStageComplete = (
  stage: { status?: string; data?: { status?: string } } | null | undefined
): boolean => {
  if (!stage) return false;
  const s = stage.status?.toLowerCase();
  const dataStatus = stage.data?.status?.toLowerCase();
  return s === "success" || s === "approved" || dataStatus === "approved";
};

const BillPaymentSection: React.FC = () => {
  const navigate = useNavigate();
  const { data: kycDetailsData } = useKycDetails();
  const [isNotVerified, setIsNotVerified] = useState(false);

  const [openAirtime, setOpenAirtime] = useState(false);
  const [openData, setOpenData] = useState(false);
  const [openElectric, setOpenElectric] = useState(false);
  const [openAirtimePreview, setOpenAirtimePreview] = useState(false);
  const [openElectricPreview, setOpenElectricPreview] = useState(false);
  const [openDataPreview, setOpenDataPreview] = useState(false);
  const [openAirtimeSuccess, setOpenAirtimeSuccess] = useState(false);
  const [openDataSuccess, setOpenDataSuccess] = useState(false);
  const [openElectricSuccess, setOpenElectricSuccess] = useState(false);
  const [openBetting, setOpenBetting] = useState(false);
  const [openBettingPreview, setOpenBettingPreview] = useState(false);
  const [openBettingSuccess, setOpenBettingSuccess] = useState(false);

  const [amount, setAmount] = useState("");
  const [electricAmount, setElectricAmount] = useState("");
  const [dataAmount, setDataAmount] = useState("");
  const [phone, setPhone] = useState("");
  const [electricPhone, setElectricPhone] = useState("");
  const [dataPhone, setDataPhone] = useState("");
  const [dataNetwork, setDataNetwork] = useState("");
  const [dataNetworkName, setDataNetworkName] = useState("");
  const [dataProductSlug, setDataProductSlug] = useState("");
  const [dataPaymentSource, setDataPaymentSource] = useState<"NGN" | "USDT">(
    DEFAULT_UTILITY_PAYMENT_SOURCE
  );
  const [network, setNetwork] = useState("");
  const [networkName, setNetworkName] = useState("");
  const [airtimePaymentSource, setAirtimePaymentSource] = useState<
    "NGN" | "USDT"
  >(DEFAULT_UTILITY_PAYMENT_SOURCE);
  const [planName, setPlanName] = useState("");
  const [meterNo, setMeterNo] = useState("");
  const [electricProviderName, setElectricProviderName] = useState("");
  const [electricProductSlug, setElectricProductSlug] = useState("");
  const [electricPaymentSource, setElectricPaymentSource] = useState<
    "NGN" | "USDT"
  >(DEFAULT_UTILITY_PAYMENT_SOURCE);
  const [electricCustomerName, setElectricCustomerName] = useState("");
  const [electricReceipt, setElectricReceipt] =
    useState<ElectricityReceipt | null>(null);
  const [betId, setBetId] = useState("");
  const [customerId, setCustomerId] = useState("");
  const [betAmount, setBetAmount] = useState("");
  const [providerName, setProviderName] = useState("");
  const [customerName, setCustomerName] = useState("");
  const [bettingPaymentSource, setBettingPaymentSource] =
    useState<BettingPaymentSource>(DEFAULT_UTILITY_PAYMENT_SOURCE);

  const details = kycDetailsData?.kycDetails;
  const level1Complete = isStageComplete(details?.first_stage);

  useEffect(() => {
    setIsNotVerified(!level1Complete);
  }, [level1Complete]);

  const handleOpenKycOnboarding = () => {
    navigate("/kyc-verification");
  };

  const guardUtility = (open: () => void) => {
    if (isNotVerified) {
      handleOpenKycOnboarding();
    } else {
      open();
    }
  };

  return (
    <>
      <div className="mt-8 w-full">
        <h3 className="text-[15px] font-bold text-paragraph dark:text-white">
          Bill Payment
        </h3>
        <div className="grid w-full grid-cols-4 gap-2 mt-4 ">
          <button
            type="button"
            onClick={() => guardUtility(() => setOpenAirtime(true))}
            className="flex flex-col items-center justify-center rounded-xl border bg-white dark:bg-[#1C2227] border-gray-100 py-2 shadow-sm dark:border-gray-700"
          >
            <BiPhone className="text-[24px] text-secondary_button" />
            <h4 className="mt-1 text-[12px] font-semibold text-[#797979] dark:text-gray-400">
              Airtime
            </h4>
          </button>
          <button
            type="button"
            onClick={() => guardUtility(() => setOpenData(true))}
            className="flex flex-col items-center justify-center rounded-xl border bg-white dark:bg-[#1C2227] border-gray-100 py-2 shadow-sm dark:border-gray-700"
          >
            <RiCompass3Line className="text-[24px] text-secondary_button" />
            <h4 className="mt-1 text-[12px] font-semibold text-[#797979] dark:text-gray-400">
              Data
            </h4>
          </button>
          <button
            type="button"
            onClick={() => guardUtility(() => setOpenElectric(true))}
            className="flex flex-col items-center justify-center rounded-xl border bg-white dark:bg-[#1C2227] border-gray-100 py-2 shadow-sm dark:border-gray-700"
          >
            <LuLightbulb className="text-[24px] text-secondary_button" />
            <h4 className="mt-1 text-[12px] font-semibold text-[#797979] dark:text-gray-400">
              Electricity
            </h4>
          </button>
          <button
            type="button"
            onClick={() => guardUtility(() => setOpenBetting(true))}
            className="flex flex-col items-center justify-center rounded-xl border bg-white dark:bg-[#1C2227] border-gray-100 py-2 shadow-sm dark:border-gray-700"
          >
            <PiTrophyBold className="text-[24px] text-secondary_button" />
            <h4 className="mt-1 text-[12px] font-semibold text-[#797979] dark:text-gray-400">
              Betting
            </h4>
          </button>
        </div>
      </div>

      {openAirtime && (
        <BuyAirtime
          setOpenAirtime={setOpenAirtime}
          setOpenAirtimePreview={setOpenAirtimePreview}
          amount={amount}
          setAmount={setAmount}
          phone={phone}
          setPhone={setPhone}
          network={network}
          setNetwork={setNetwork}
          networkName={networkName}
          setNetworkName={setNetworkName}
          paymentSource={airtimePaymentSource}
          setPaymentSource={setAirtimePaymentSource}
        />
      )}
      {openAirtimePreview && (
        <AirtimePreview
          setOpenAirtimePreview={setOpenAirtimePreview}
          setOpenAirtime={setOpenAirtime}
          setOpenAirtimeSuccess={setOpenAirtimeSuccess}
          amount={amount}
          phone={phone}
          network={network}
          networkName={networkName}
          paymentSource={airtimePaymentSource}
        />
      )}
      {openAirtimeSuccess && (
        <BuySuccess
          amount={amount}
          phone={phone}
          network={networkName || network}
          setOpenAirtimeSuccess={setOpenAirtimeSuccess}
          setOpenAirtime={setOpenAirtime}
        />
      )}

      {openData && (
        <BuyData
          setOpenData={setOpenData}
          amount={dataAmount}
          phone={dataPhone}
          network={dataNetwork}
          networkName={dataNetworkName}
          setAmount={setDataAmount}
          setPhone={setDataPhone}
          setNetwork={setDataNetwork}
          setNetworkName={setDataNetworkName}
          planName={planName}
          setPlanName={setPlanName}
          productSlug={dataProductSlug}
          setProductSlug={setDataProductSlug}
          paymentSource={dataPaymentSource}
          setPaymentSource={setDataPaymentSource}
          setOpenDataPreview={setOpenDataPreview}
        />
      )}
      {openDataPreview && (
        <DataPreview
          setOpenData={setOpenData}
          amount={dataAmount}
          phone={dataPhone}
          network={dataNetwork}
          networkName={dataNetworkName}
          planName={planName}
          productSlug={dataProductSlug}
          paymentSource={dataPaymentSource}
          setOpenDataPreview={setOpenDataPreview}
          setOpenDataSuccess={setOpenDataSuccess}
        />
      )}
      {openDataSuccess && (
        <DataSuccess
          amount={dataAmount}
          phone={dataPhone}
          planName={planName}
          setOpenData={setOpenData}
          setOpenDataSuccess={setOpenDataSuccess}
        />
      )}

      {openElectric && (
        <BuyElectricity
          setOpenElectric={setOpenElectric}
          setOpenElectricPreview={setOpenElectricPreview}
          meterNo={meterNo}
          setMeterNo={setMeterNo}
          providerName={electricProviderName}
          setProviderName={setElectricProviderName}
          productSlug={electricProductSlug}
          setProductSlug={setElectricProductSlug}
          electricPhone={electricPhone}
          setElectricPhone={setElectricPhone}
          electricAmount={electricAmount}
          setElectricAmount={setElectricAmount}
          paymentSource={electricPaymentSource}
          setPaymentSource={setElectricPaymentSource}
          setElectricCustomerName={setElectricCustomerName}
        />
      )}
      {openElectricPreview && (
        <ElectricPreview
          setOpenElectric={setOpenElectric}
          setOpenElectricPreview={setOpenElectricPreview}
          meterNo={meterNo}
          providerName={electricProviderName}
          productSlug={electricProductSlug}
          electricPhone={electricPhone}
          electricAmount={electricAmount}
          paymentSource={electricPaymentSource}
          electricCustomerName={electricCustomerName}
          setOpenElectricSuccess={setOpenElectricSuccess}
          setElectricReceipt={setElectricReceipt}
        />
      )}
      {openElectricSuccess && (
        <ElectricSuccess
          setOpenElectric={setOpenElectric}
          meterNo={meterNo}
          serviceProvider={electricProviderName}
          electricPhone={electricPhone}
          electricAmount={electricAmount}
          setOpenElectricSuccess={setOpenElectricSuccess}
          electricReceipt={electricReceipt}
        />
      )}

      {openBetting && (
        <Betting
          setOpenBetting={setOpenBetting}
          setBetId={setBetId}
          setCustomerId={setCustomerId}
          setBetAmount={setBetAmount}
          betAmount={betAmount}
          customerId={customerId}
          betId={betId}
          providerName={providerName}
          setProviderName={setProviderName}
          setCustomerName={setCustomerName}
          customerName={customerName}
          setOpenBettingPreview={setOpenBettingPreview}
          paymentSource={bettingPaymentSource}
          setPaymentSource={setBettingPaymentSource}
        />
      )}
      {openBettingPreview && (
        <BettingPreview
          setOpenBetting={setOpenBetting}
          setOpenBettingPreview={setOpenBettingPreview}
          betAmount={betAmount}
          customerId={customerId}
          betId={betId}
          providerName={providerName}
          customerName={customerName}
          paymentSource={bettingPaymentSource}
          setOpenBettingSuccess={setOpenBettingSuccess}
        />
      )}
      {openBettingSuccess && (
        <BettingSuccess
          betAmount={betAmount}
          providerName={providerName}
          customerName={customerName}
          setOpenBetting={setOpenBetting}
          setOpenBettingSuccess={setOpenBettingSuccess}
        />
      )}
    </>
  );
};

export default BillPaymentSection;
